/**
 * OnboardingStep4.tsx - Motivations & Semester Goal
 * Fourth step of the onboarding flow.
 * Collects user motivations (multi-select) and semester goal (dropdown).
 * Goal options are translated dynamically from the backend.
 */
import React from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import OnboardingStepHeader from "./common/OnboardingStepHeader";
import OnboardingFormSection from "./common/OnboardingFormSection";
import CustomDropdown from "../../common/general/CustomDropDown/CustomDropdown";
import ChipSelector, { ChipOption } from "../../common/form/ChipSelect/ChipSelector";
import { spacingStyles } from "../../../styles/design-system";

export interface OnboardingStep4Props {
  selectedMotivations: string[];
  onMotivationsChange: (motivations: string[]) => void;
  selectedGoal: string;
  onGoalChange: (goal: string) => void;
  goalOptions: { key: string; value: string }[];
}

const motivationOptions: ChipOption[] = [
  { key: "better_grades", value: "Better Grades" },
  { key: "deep_understanding", value: "Deep Understanding" },
  { key: "more_confidence", value: "More Confidence" },
  { key: "learning_faster", value: "Learning Faster" },
];

function OnboardingStep4({
  selectedMotivations,
  onMotivationsChange,
  selectedGoal,
  onGoalChange,
  goalOptions,
}: OnboardingStep4Props) {
  const { t } = useTranslation();

  return (
    <View style={spacingStyles.mNone}>
      <OnboardingStepHeader
        titleKey="companion_onboarding_step4_title"
        subtitleKey="companion_onboarding_step4_subtitle"
      />

      <OnboardingFormSection labelKey="companion_onboarding_step4_motivations_label" isUppercase>
        <ChipSelector
          options={motivationOptions}
          selectedKeys={selectedMotivations}
          onSelectionChange={onMotivationsChange}
          multiSelect
        />
      </OnboardingFormSection>

      <OnboardingFormSection labelKey="companion_onboarding_step4_goal_label" isUppercase>
        <CustomDropdown
          options={goalOptions.map((opt) => ({ label: opt.value, value: opt.key }))}
          value={selectedGoal}
          onChange={onGoalChange}
          placeholder={t("text.companion_onboarding_step4_goal_placeholder")}
          containerStyle={{ width: "100%" }}
          hideItemBorders
        />
      </OnboardingFormSection>
    </View>
  );
}

export default OnboardingStep4;
